import React, {useCallback, useEffect, useRef, useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {
  EndIconsWrapper,
  InputTextType,
  MD_INPUT_HEIGHT,
  StartIconsWrapper
} from '@vio/atlas/helpers/InputBase'
import {InputButtonStyledContainer} from '@vio/atlas/helpers/InputButton'
import {Icon} from '@vio/atlas/Icon'
import {InputText} from '@vio/atlas/InputText'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import useDebounce from '@vio/core/src/utils/debounce/hooks/useDebounce'

interface Props {
  hasError?: boolean
  /** Destination name */
  destination?: string
  /** Whether the destination picker popover is currently open */
  isDestinationPickerOpen: boolean
  /** Callback for when autocomplete data is requested */
  onSuggestionsRequested: (
    value: string,
    shouldLoadUserSearchHistory: boolean
  ) => void
  /** Whether search history should be shown */
  shouldLoadUserSearchHistory?: boolean
  /** Whether the input should be focused on mount */
  focusOnMount?: boolean
  /** Whether the input icon label should be replaced with a text label */
  isTextLabel?: boolean
}

export const destinationPickerMessages = defineMessages({
  destinationPlaceholder: {
    id: 'whereAreYouGoing',
    defaultMessage: 'Where are you going?'
  }
})

export const DestinationLabelWrapper = styled(Text)`
  white-space: nowrap;
  color: ${cssTheme.colors.input.default.icon};
`

const IconWrapper = styled.div``

export const DestinationInput = styled(InputText)`
  label.--is-invalid {
    border-color: ${cssTheme.colors.input.error.border};
    outline-color: ${cssTheme.colors.input.error.border};
  }

  background: ${cssTheme.colors.input.default.background};

  ${InputButtonStyledContainer} {
    background: initial;
  }
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: auto;
  padding: 0;
  height: ${MD_INPUT_HEIGHT}px;
  label {
    padding: 0 10px;
  }
  input {
    border-radius: ${cssTheme.layout.radius.md};
    background: ${cssTheme.colors.input.default.background} !important;
    ${cssTheme.typography.bodyS};

    border-color: ${cssTheme.colors.input.focus.border};

    &::-webkit-search-cancel-button {
      display: none;
    }
  }
  ${StartIconsWrapper} {
    padding-left: ${cssTheme.layout.spacing.s400};
  }
  ${IconWrapper} {
    color: ${cssTheme.colors.content.interactive.primary} !important;
  }
  ${EndIconsWrapper} {
    padding-right: ${cssTheme.layout.spacing.s100};
  }
`

export const SearchFormDestinationInput = React.memo(
  function SearchFormDestinationInput({
    destination,
    hasError = false,
    isDestinationPickerOpen,
    onSuggestionsRequested,
    shouldLoadUserSearchHistory = false,
    focusOnMount = false,
    isTextLabel = false
  }: Props) {
    const {formatMessage} = useIntl()
    const labelRef = useRef<null | HTMLDivElement>(null)
    const inputFieldRef = useRef<null | HTMLInputElement>(null)
    const [inputValue, setInputValue] = useState(destination)
    const debouncedInputValue = useDebounce(inputValue, 250)

    useEffect(() => {
      if (focusOnMount) inputFieldRef.current?.focus?.()
    }, [focusOnMount])

    useEffect(() => {
      setInputValue(destination)
    }, [setInputValue, destination])

    // Requesting suggestions can cause a loop when we rely on dep stability so check that value is actually changed
    const previousDebouncedInputValue = useRef<string | undefined>(
      debouncedInputValue
    )

    useEffect(() => {
      const isChanged =
        debouncedInputValue !== previousDebouncedInputValue.current
      if (!isChanged) return

      previousDebouncedInputValue.current = debouncedInputValue

      onSuggestionsRequested(
        debouncedInputValue ?? '',
        Boolean(!debouncedInputValue)
      )
    }, [debouncedInputValue, onSuggestionsRequested])

    const handleInputChange = useCallback(
      (value: string) => {
        setInputValue(value)
      },
      [setInputValue]
    )

    const handleInputFocus = useCallback(
      ({target}: React.FocusEvent<HTMLInputElement>) => {
        const {value} = target

        if (destination === '') {
          onSuggestionsRequested('', true)
        } else {
          onSuggestionsRequested(value, shouldLoadUserSearchHistory)
        }
      },
      [destination, shouldLoadUserSearchHistory, onSuggestionsRequested]
    )

    const handleClearDestination = useCallback(
      (event: React.SyntheticEvent) => {
        event.preventDefault()

        setInputValue('')
        inputFieldRef.current?.focus()
      },
      [inputFieldRef, setInputValue]
    )

    return (
      <DestinationInput
        ref={inputFieldRef}
        isInvalid={hasError}
        customProps={{
          role: 'presentation',
          'aria-invalid': hasError || undefined,
          autoCapitalize: 'off',
          autoCorrect: 'off',
          spellCheck: 'false',
          autocomplete: 'off',
          dataLpignore: true,
          dataFormType: 'other'
        }}
        id="DestinationInput"
        dataId="DestinationInput"
        name="DestinationInput"
        onFocus={handleInputFocus}
        prefix={
          !isTextLabel ? (
            <IconWrapper>
              <Icon name="Pin" />
            </IconWrapper>
          ) : (
            <div ref={labelRef}>
              <DestinationLabelWrapper variant="labelS">
                <FormattedMessageWrapper
                  id="location"
                  defaultMessage="Location"
                />
              </DestinationLabelWrapper>
            </div>
          )
        }
        placeholder={formatMessage(
          destinationPickerMessages.destinationPlaceholder
        )}
        type={InputTextType.search}
        value={inputValue}
        onChange={handleInputChange}
        suffix={
          inputValue && (
            <Button
              onClick={handleClearDestination}
              variant="ghost"
              size="sm"
              dataId="DestinationInputClear"
              iconStart={
                <Icon
                  name="Clear"
                  size="sm"
                  colorPath="content.neutral.secondary"
                />
              }
            />
          )
        }
        className={cx(isDestinationPickerOpen && '--is-active')}
      />
    )
  }
)
